import type { MembershipRole } from "./queries/memberships";
import type { ID } from "./id";

/**
 * Czech collation. `Intl` knows the alphabet the app is written for: č
 * sorts after c, ch after h, ř after r, š after s, ž after z — none of
 * which plain code-point ordering gets right, and all of which a Czech
 * reader notices immediately in a name list.
 *
 * Built once: constructing a collator per comparison turns an O(n log n)
 * sort into an expensive one.
 */
const czech = new Intl.Collator("cs", { usage: "sort" });

/** The shared Czech comparator — any name-ordering surface should use it
 * rather than constructing its own collator. */
export const czechCompare = czech.compare;

/** The shape the ordering needs — any roster row satisfies it. */
export interface RosterRow {
  person: { id: ID; first_name: string; last_name: string };
  roles: MembershipRole[];
}

/**
 * Which block of the roster a row belongs to.
 *
 * The leader heads the list because the team is answerable through them,
 * then the people who run the training (trenéři and asistenti together —
 * the distinction is visible in the role column and does not need to
 * split the list), then everyone else.
 *
 * Exported because the roster's „Role" column sorts by it: clicking that
 * header must reproduce the order the table already opens in, and a second
 * copy of the rule would be a second answer to the same question.
 */
export function rosterGroup(row: RosterRow, leaderPersonId: ID | undefined): number {
  if (leaderPersonId !== undefined && row.person.id === leaderPersonId) return 0;
  return row.roles.some((r) => r === "coach" || r === "assistant") ? 1 : 2;
}

/**
 * Roster order: leader, then coaches and assistants, then members —
 * each block by surname in Czech collation.
 *
 * Returns a new array; the roster comes from a query cache and must not
 * be sorted in place.
 */
export function sortRoster<T extends RosterRow>(
  rows: readonly T[],
  leaderPersonId?: ID,
): T[] {
  return [...rows].sort((a, b) => {
    const byGroup = rosterGroup(a, leaderPersonId) - rosterGroup(b, leaderPersonId);
    if (byGroup !== 0) return byGroup;
    const byLast = czech.compare(a.person.last_name, b.person.last_name);
    if (byLast !== 0) return byLast;
    const byFirst = czech.compare(a.person.first_name, b.person.first_name);
    // Namesakes still need a fixed order, or the table reshuffles on every
    // refetch.
    return byFirst !== 0 ? byFirst : a.person.id < b.person.id ? -1 : a.person.id > b.person.id ? 1 : 0;
  });
}
